import { formatLocalDate } from '../utils/localDateTime';
import { showAppAlert } from '../common/appDialog';
import React, { useState, useEffect, useMemo } from 'react';
import { ICONS } from './constants';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, CartesianGrid } from 'recharts';
import type { ReportConfig } from './ExecutiveReportBuilder';
import { db } from '../../firebaseConfig';
import { collection, addDoc, query, where, getDocs, limit, orderBy, doc, onSnapshot } from 'firebase/firestore';
import { UnifiedMap } from './UnifiedMap';
import useGoogleMaps from './useGoogleMaps';
import { useGlobalAssets } from '../context/GlobalAssetContext';
import { UserProfile, Organization, GlobalAsset, ThreatAlert } from '@types';
import { printRenderedReport } from '../utils/printRenderedReport';
import { getOrganizationDisplayName } from '@common/organizationDisplayName';
import { normalizeFeedEvent } from '../utils/riskEventFeed';

interface ExecutiveReportViewerProps {
    config?: ReportConfig;
    reportData?: any;
    reportId?: string;
    onClose: () => void;
    currentUser: UserProfile | null;
    currentOrganization: Organization | null;
}

export const ExecutiveReportViewer: React.FC<ExecutiveReportViewerProps> = ({ config, reportData: initialReportData, reportId, onClose, currentUser, currentOrganization }) => {
    const { isLoaded } = useGoogleMaps();
    const { assets } = useGlobalAssets();
    
    const [activeImpactTab, setActiveImpactTab] = useState<'operations' | 'personnel' | 'assets' | 'travel' | 'userEngagement'>('operations');
    const [isSaving, setIsSaving] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [countryAnalysis, setCountryAnalysis] = useState<any>(null);
    const [activeFrameworks, setActiveFrameworks] = useState<any[]>([]);
    const [recentAlerts, setRecentAlerts] = useState<ThreatAlert[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [reportData, setReportData] = useState<any>(initialReportData);
    const [generationStatus, setGenerationStatus] = useState(initialReportData ? 'completed' : reportId ? 'processing' : 'completed');
    const [generationError, setGenerationError] = useState('');

    useEffect(() => {
        if (initialReportData) {
            setReportData(initialReportData);
            setGenerationStatus('completed');
        }
    }, [initialReportData]);

    useEffect(() => {
        if (!db || !reportId)
            return;

        return onSnapshot(doc(db, 'generated_executive_reports', reportId), snapshot => {
            if (!snapshot.exists()) {
                setGenerationStatus('failed');
                setGenerationError('The requested executive report could not be found.');
                return;
            }
            const record = snapshot.data();
            const status = String(record.status || 'processing');
            setGenerationStatus(status);
            if (status === 'completed' && record.reportData) {
                setReportData(record.reportData);
                setGenerationError('');
            }
            if (status === 'failed')
                setGenerationError(record.error || 'Executive report generation failed.');
        }, error => {
            setGenerationStatus('failed');
            setGenerationError(error.message || 'Executive report status could not be loaded.');
        });
    }, [reportId]);

    useEffect(() => {
        const fetchData = async () => {
            if (!db || !currentOrganization) return;
            setIsLoading(true);
            const loadCountryAnalysis = async () => {
                try {
                const countryQuery = query(
                    collection(db, 'daily_country_analysis'),
                    where('targetRegion', '==', currentOrganization.region || 'Global'),
                    orderBy('timestamp', 'desc'),
                    limit(1)
                );
                const countrySnap = await getDocs(countryQuery);
                if (!countrySnap.empty) {
                    setCountryAnalysis(countrySnap.docs[0].data());
                }
                } catch (error) {
                    console.warn('Executive report country context unavailable:', error);
                }
            };

            const loadFrameworks = async () => {
                try {
                const frameworkQuery = query(
                    collection(db, 'srm_projects'),
                    where('organizationId', '==', currentOrganization.id),
                    where('status', '==', 'active'),
                    limit(5)
                );
                const frameworkSnap = await getDocs(frameworkQuery);
                setActiveFrameworks(frameworkSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                } catch (error) {
                    console.warn('Executive report framework context unavailable:', error);
                }
            };

            const loadRiskEvents = async () => {
                try {
                const alertsQuery = query(
                    collection(db, 'risk_events'),
                    orderBy('processedAt', 'desc'),
                    limit(15)
                );
                const alertsSnap = await getDocs(alertsQuery);
                setRecentAlerts(alertsSnap.docs.map(document => {
                    const data = document.data();
                    const geo = data.geo || {};
                    const location = data.location || {};
                    const event = normalizeFeedEvent(document.id, data);
                    return {
                        ...event,
                        id: document.id,
                        timestamp: data.processedAt?.toMillis?.() || Date.parse(data.processedAt || '') || Date.now(),
                        category: data.category || data.analysis?.category || 'General',
                        severity: data.alertSeverity || event.severity || 'Unknown',
                        summary: data.summary || data.analysis?.incident_summary || data.content || 'Risk event detected.',
                        location: {
                            city: location.city || geo.location_name || 'Global',
                            country: location.country || geo.country_iso3 || data.iso3 || 'Global',
                            lat: Number(location.lat ?? geo.lat ?? geo.latitude ?? data.lat ?? 0),
                            lng: Number(location.lng ?? geo.lng ?? geo.longitude ?? data.lng ?? 0)
                        },
                        sources: data.sources || []
                    } as ThreatAlert;
                }));
                } catch (error) {
                    console.warn('Executive report risk context unavailable:', error);
                }
            };

            try {
                await Promise.all([loadCountryAnalysis(), loadFrameworks(), loadRiskEvents()]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [currentOrganization]);

    if (reportId && generationStatus !== 'completed') {
        const failed = generationStatus === 'failed';
        return (
            <div className="fixed inset-0 z-50 flex min-h-screen items-center justify-center bg-slate-950 p-6 text-white">
                <div className="w-full max-w-xl rounded-2xl border border-slate-700 bg-slate-900 p-10 text-center shadow-2xl">
                    {failed ? (
                        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-500/15 text-3xl text-red-300">!</div>
                    ) : (
                        <div className="mx-auto mb-6 h-16 w-16 animate-spin rounded-full border-4 border-blue-500/20 border-t-blue-400" />
                    )}
                    <h1 className="text-2xl font-black uppercase tracking-widest">
                        {failed ? 'Report Generation Failed' : 'Building Executive Report'}
                    </h1>
                    <p className="mt-4 text-sm leading-relaxed text-slate-300">
                        {failed
                            ? generationError
                            : 'The intelligence worker is gathering organizational context, validating sources, and synthesizing your selected report sections.'}
                    </p>
                    {!failed && (
                        <p className="mt-6 font-mono text-xs uppercase tracking-widest text-blue-300">
                            This screen updates automatically
                        </p>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        className="mt-8 rounded-lg border border-slate-600 px-6 py-3 text-xs font-black uppercase tracking-widest text-slate-200 transition-colors hover:bg-slate-800"
                    >
                        {failed ? 'Return to Dashboard' : 'Continue in Background'}
                    </button>
                </div>
            </div>
        );
    }

    const handleSave = async () => {
        if (!db || !reportData) return;
        setIsSaving(true);
        try {
            await addDoc(collection(db, 'saved_intel'), {
                ...reportData,
                reportConfig: config,
                type: 'Executive Report',
                title: `Executive Report: ${config?.timeframe || 'Standard'}`,
                reportTitle: `Executive Operational Impact - ${formatLocalDate(new Date())}`,
                savedAt: new Date().toISOString()
            });
            setIsSaved(true);
            void showAppAlert("Report saved successfully to Intelligence Library.");
        } catch (error) {
            console.error("Error saving report:", error);
            void showAppAlert("Failed to save report.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleProDownloadPDF = async () => {
        printRenderedReport(`Executive Report — ${getOrganizationDisplayName(currentOrganization)}`);
    };

    // Extract Dynamic Data (Fallback to Empty State if missing)
    const execSummary = reportData?.executiveSummary || "Geopolitical and operational analysis summary is currently being compiled from real-time data feeds.";
    const userEngagement = reportData?.userEngagement || {
        summary: "Platform engagement metrics currently unavailable for this timeframe.",
        stats: [
            { label: "Active Intelligence Users", value: "0", trend: "none" },
            { label: "Framework Protocols Activated", value: "0", trend: "none" },
            { label: "Average Response Time", value: "N/A", trend: "none" }
        ],
        topWorkflows: []
    };
    const subSummary = reportData?.subSummary || "";
    const recs = reportData?.recommendations || [
        "No specific strategic recommendations generated for the current data set."
    ];
    
    const pestleData = reportData?.pestle || {};
    const pmesiiData = reportData?.pmesii || {};

    const predictiveData = reportData?.predictiveData || [];

    const carverData = reportData?.carverData || [
        { subject: 'Criticality', A: 0, fullMark: 100 },
        { subject: 'Accessibility', A: 0, fullMark: 100 },
        { subject: 'Recuperability', A: 0, fullMark: 100 },
        { subject: 'Vulnerability', A: 0, fullMark: 100 },
        { subject: 'Effect', A: 0, fullMark: 100 },
        { subject: 'Recognizability', A: 0, fullMark: 100 },
    ];

    const impactMatrix = reportData?.impactMatrix || {
        operations: [],
        personnel: [],
        assets: [],
        travel: []
    };
    const pillarAnalysis = reportData?.pillarAnalysis || {};
    const pillarLabels: Record<string, string> = {
        intelligence: 'Geopolitical Intelligence',
        security: 'Security Risk Management',
        resilience: 'Business Resilience',
        learning: 'Learning Management',
        userStatistics: 'User Statistics'
    };

    return (
        <div className="analysis-report-print-area min-h-screen bg-slate-50 text-slate-800 font-sans selection:bg-blue-500/30 overflow-y-auto w-full absolute inset-0 z-50 animate-fade-in shadow-2xl">
            {/* Top Command Bar (Hidden during print) */}
            <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 px-6 py-4 flex justify-between items-center print:hidden shadow-sm">
                <div className="flex items-center space-x-4">
                    <button onClick={onClose} className="text-slate-600 hover:text-blue-600 transition-colors flex items-center text-sm font-bold uppercase tracking-widest group">
                        <svg className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Exit Report
                    </button>
                    <div className="h-6 w-px bg-slate-200 mx-4"></div>
                    <span className="text-blue-400 font-mono text-xs uppercase tracking-widest bg-blue-500/10 px-3 py-1 rounded border border-blue-500/20 shadow-[0_0_10px_rgba(59,130,246,0.1)]">
                        {config?.timeframe || 'YTD'} Analysis Active
                    </span>
                    {isLoading && (
                        <div className="flex items-center space-x-2 animate-pulse">
                            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                            <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest">Updating Context...</span>
                        </div>
                    )}
                </div>
                <div className="flex space-x-3">
                    <button 
                        onClick={handleSave} 
                        disabled={isSaving || isSaved}
                        className={`px-4 py-2 rounded text-xs font-black uppercase tracking-widest transition-all flex items-center border ${
                            isSaved 
                            ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30 shadow-[0_0_15px_rgba(16,185,129,0.1)]' 
                            : 'bg-indigo-600 hover:bg-indigo-500 text-white border-indigo-400/50 shadow-lg shadow-indigo-500/20'
                        }`}
                    >
                        {isSaving ? (
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                        ) : (
                            <span className="mr-2">{isSaved ? '✓' : ICONS.document}</span>
                        )}
                        {isSaved ? 'Saved to Library' : 'Save to Library'}
                    </button>
                    <button 
                        onClick={handleProDownloadPDF}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-black uppercase tracking-widest transition-all flex items-center border border-blue-400/50 shadow-lg shadow-blue-500/20"
                    >
                        <span className="mr-2">{ICONS.download}</span>
                        Print / Save PDF
                    </button>
                </div>
            </div>

            {/* Document Body */}
            <div className="max-w-6xl mx-auto p-8 md:p-12 print:p-0 bg-transparent print:bg-white print:text-black">
                
                {/* Header Sequence */}
                <div className="border-b border-slate-200 print:border-slate-300 pb-12 mb-12 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/5 blur-[120px] rounded-full print:hidden"></div>
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-600/5 blur-[100px] rounded-full print:hidden"></div>
                    
                    <div className="flex flex-col md:flex-row justify-between items-start gap-8 relative z-10">
                        <div>
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="p-2 bg-blue-500 rounded shadow-[0_0_15px_rgba(59,130,246,0.5)]">
                                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                    </svg>
                                </div>
                                <span className="text-xs font-black text-blue-500 uppercase tracking-[0.4em]">Integrated Intelligence Suite</span>
                            </div>
                            <h1 className="text-5xl md:text-6xl font-black text-slate-900 print:text-black tracking-tight leading-[1.1]">
                                Enterprise Control <br/> 
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-blue-500 print:text-indigo-600">
                                    {reportData?.analysisType === 'intelligence_report' ? 'Strategic Intelligence' : 'Executive Report'}
                                </span>
                            </h1>
                            <p className="text-xl text-slate-600 print:text-slate-600 font-light mt-6 max-w-2xl leading-relaxed">
                                Strategic oversight, predictive threats, and operational framework analysis for <span className="text-slate-900 font-bold">{getOrganizationDisplayName(currentOrganization)}</span>.
                            </p>
                        </div>
                        <div className="text-left md:text-right w-full md:w-auto">
                            <div className="bg-white border border-slate-200 p-6 rounded-xl shadow-sm">
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-2">Report Context</p>
                                <p className="font-mono text-lg text-slate-900 print:text-slate-800 mb-4">{formatLocalDate(new Date())}</p>
                                <div className="inline-block bg-red-500/10 print:bg-red-50 border border-red-500/30 print:border-red-500 px-4 py-2 rounded-lg">
                                    <p className="text-[10px] font-black text-red-500 uppercase tracking-widest flex items-center">
                                        <span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2 animate-pulse"></span>
                                        Strictly Confidential
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- GEOSPATIAL ASSET & RISK MAPPING --- */}
                <section className="mb-16">
                    <div className="flex flex-col md:flex-row justify-between items-end mb-8 gap-4">
                        <div>
                            <div className="flex items-center space-x-2 mb-2">
                                <div className="w-8 h-[1px] bg-blue-500"></div>
                                <h2 className="text-xs font-black text-blue-500 uppercase tracking-[0.3em]">{currentOrganization?.region || 'GLOBAL'} INTELLIGENCE</h2>
                            </div>
                            <h3 className="text-4xl font-black text-white leading-none">Geospatial Risk Footprint</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-8 text-right bg-white/5 p-4 rounded-xl border border-white/10 backdrop-blur-sm">
                            <div>
                                <span className="text-[10px] font-black text-slate-500 uppercase block mb-1">Active Assets</span>
                                <span className="text-3xl font-black text-white leading-none">{assets.length}</span>
                            </div>
                            <div>
                                <span className="text-[10px] font-black text-slate-500 uppercase block mb-1">Live Alerts</span>
                                <span className="text-3xl font-black text-red-500 leading-none">{recentAlerts.length}</span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="h-[500px] rounded-2xl overflow-hidden border border-white/10 bg-slate-900 shadow-[0_20px_50px_rgba(0,0,0,0.5)] relative group">
                        {isLoaded ? (
                            <UnifiedMap 
                                isLoaded={isLoaded}
                                assets={assets}
                                alerts={recentAlerts.map(a => ({
                                    ...a,
                                    id: a.id,
                                    lat: a.location?.lat,
                                    lng: a.location?.lng,
                                    severity: a.severity,
                                    type: a.category,
                                    title: a.summary
                                }))}
                                onAlertClick={() => {}}
                                initialViewState={{ latitude: assets[0]?.location?.lat || 20, longitude: assets[0]?.location?.lng || 0, zoom: 3 }}
                            />
                        ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center bg-slate-900/50 space-y-4">
                                <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
                                <p className="text-xs font-black text-slate-500 uppercase tracking-widest">Loading Geospatial Engine...</p>
                            </div>
                        )}
                        <div className="absolute inset-0 pointer-events-none border border-white/5 rounded-2xl"></div>
                        <div className="absolute bottom-6 left-6 right-6 flex flex-col md:flex-row justify-between items-end md:items-center pointer-events-none gap-4">
                            <div className="bg-[#0B1121]/90 backdrop-blur-xl border border-white/10 p-4 rounded-xl flex space-x-8 pointer-events-auto shadow-2xl transition-transform transform translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 duration-500">
                                <div className="flex items-center space-x-3">
                                    <div className="w-3 h-3 rounded-full bg-blue-500 shadow-[0_0_12px_rgba(59,130,246,0.8)]"></div>
                                    <span className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">Enterprise Assets</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <div className="w-3 h-3 rounded-full bg-red-600 animate-pulse shadow-[0_0_12px_rgba(220,38,38,0.8)]"></div>
                                    <span className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">Verified Threats</span>
                                </div>
                            </div>
                            <div className="bg-[#060913]/90 backdrop-blur-md border border-emerald-500/30 px-4 py-2 rounded-lg text-[10px] font-black text-emerald-400 uppercase tracking-[0.3em] pointer-events-auto shadow-xl flex items-center">
                                <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-ping"></span>
                                ECC LIVE SYNC ACTIVE
                            </div>
                        </div>
                    </div>
                    <div className="mt-6 flex items-start space-x-4">
                        <div className="mt-1 p-1 bg-blue-500/10 rounded">
                            <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <p className="text-xs text-slate-500 font-mono leading-relaxed max-w-3xl">
                            <span className="text-slate-400 font-bold uppercase tracking-widest">Analyst Brief:</span> Integrated visualization of physical footprint (Facilities, Personnel, Logistics) against real-time humanitarian and security datasets. Proximity analysis enabled for all assets within 50km of active risk clusters.
                        </p>
                    </div>
                </section>

                {/* --- LATEST THREAT & OPERATING FRAMEWORK SNAPSHOT --- */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
                    {/* Country Threat Analysis Summary */}
                    <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm relative overflow-hidden group">
                        <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                            <svg className="w-24 h-24 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
                            </svg>
                        </div>
                        <h4 className="text-[10px] font-black text-blue-600 uppercase tracking-[0.4em] mb-4">Regional Risk Profile</h4>
                        <h3 className="text-3xl font-black text-slate-900 mb-6 border-b border-slate-100 pb-4">
                            {countryAnalysis?.targetRegion || currentOrganization?.region || 'Global'} <span className="text-slate-400 font-light">Threat Summary</span>
                        </h3>
                        
                        {countryAnalysis ? (
                            <div className="space-y-6">
                                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                                    <p className="text-xs text-slate-600 leading-relaxed italic line-clamp-4">
                                        "{countryAnalysis.auditData?.executiveSummary || 'No specific regional summary available for the current period.'}"
                                    </p>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-slate-100/50 p-4 rounded-xl border border-slate-200">
                                        <span className="text-[10px] font-black text-slate-500 uppercase block mb-1">Health Status</span>
                                        <span className={`text-xl font-black ${
                                            countryAnalysis.auditData?.healthStatus === 'Critical' ? 'text-red-500' : 
                                            countryAnalysis.auditData?.healthStatus === 'Warning' ? 'text-amber-500' : 'text-emerald-500'
                                        }`}>
                                            {countryAnalysis.auditData?.healthStatus || 'Stable'}
                                        </span>
                                    </div>
                                    <div className="bg-slate-100/50 p-4 rounded-xl border border-slate-200">
                                        <span className="text-[10px] font-black text-slate-500 uppercase block mb-1">Risk Score</span>
                                        <span className="text-xl font-black text-slate-900">
                                            {countryAnalysis.auditData?.overallHealthScore || 'N/A'}/100
                                        </span>
                                    </div>
                                </div>
                                <button className="w-full py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-[10px] font-black text-blue-400 uppercase tracking-widest transition-colors flex items-center justify-center">
                                    View Full Dossier <span className="ml-2">→</span>
                                </button>
                            </div>
                        ) : (
                            <div className="h-64 flex flex-col items-center justify-center text-center p-8 border-2 border-dashed border-white/5 rounded-2xl bg-white/[0.02]">
                                <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center mb-4 text-slate-700 font-black">?</div>
                                <p className="text-xs text-slate-600 font-mono">Real-time regional intelligence feed initializing...</p>
                            </div>
                        )}
                    </div>

                    {/* Business Operating Framework Overview */}
                    <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm relative overflow-hidden group">
                        <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                            <svg className="w-24 h-24 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-7h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                        </div>
                        <h4 className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.4em] mb-4">Operational Resilience</h4>
                        <h3 className="text-3xl font-black text-slate-900 mb-6 border-b border-slate-100 pb-4">
                            Business <span className="text-slate-400 font-light">Operating Framework</span>
                        </h3>
                        
                        <div className="space-y-4">
                            {activeFrameworks.length > 0 ? (
                                activeFrameworks.map((fw, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-emerald-500/30 transition-all hover:translate-x-1">
                                        <div className="flex items-center space-x-4">
                                            <div className="w-10 h-10 rounded bg-emerald-500/10 flex items-center justify-center text-emerald-600 font-black text-xs">
                                                {String(fw.name).substring(0, 2).toUpperCase()}
                                            </div>
                                            <div>
                                                <h5 className="text-sm font-black text-slate-900">{fw.name}</h5>
                                                <p className="text-[10px] text-slate-500 uppercase tracking-widest leading-none mt-1">{fw.type || 'Standard Protocol'}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="h-1.5 w-24 bg-white/10 rounded-full overflow-hidden">
                                                <div className="h-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]" style={{ width: `${fw.completion || 85}%` }}></div>
                                            </div>
                                            <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest mt-1 block">RESILIENCE SCORE</span>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="p-12 text-center border-2 border-dashed border-white/5 rounded-2xl bg-white/[0.02]">
                                    <p className="text-xs text-slate-600 font-mono">No active SRM frameworks identified in the current operating cycle.</p>
                                    <button className="mt-4 px-4 py-2 bg-emerald-600/10 text-emerald-400 border border-emerald-500/20 rounded text-[10px] font-black uppercase tracking-widest hover:bg-emerald-600/20 transition-all">
                                        Initialize Framework
                                    </button>
                                </div>
                            )}
                            
                            <div className="pt-4 border-t border-white/5 mt-4">
                                <p className="text-[10px] text-slate-400 leading-relaxed">
                                    <span className="font-black text-white uppercase tracking-widest mr-2">Advisory:</span> {activeFrameworks.length < 3 ? 'Recommend activating the "Supply Chain Resilience" suite to complement existing frameworks.' : 'Current frameworks provide 92% coverage across active risk zones.'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- INTELLIGENCE SUITE ACTIVATION & PREDICTIVE ANALYISIS --- */}
                <section className="mb-16">
                    <div className="bg-white border border-slate-200 rounded-2xl p-10 shadow-sm relative overflow-hidden">
                        {/* Decorative Background Element */}
                        <div className="absolute -top-24 -left-24 w-64 h-64 bg-indigo-600/5 blur-[80px] rounded-full"></div>
                        <div className="absolute -bottom-24 -right-24 w-64 h-64 bg-blue-600/5 blur-[80px] rounded-full"></div>
                        
                        <div className="relative z-10">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
                                <div>
                                    <h4 className="text-[10px] font-black text-blue-600 uppercase tracking-[0.4em] mb-2">Capability Assessment</h4>
                                    <h3 className="text-4xl font-black text-slate-900 leading-none">Intelligence Suite Profile</h3>
                                </div>
                                <div className="flex items-center space-x-6">
                                    <div className="text-center">
                                        <span className="text-[32px] font-black text-slate-900 leading-none">04</span>
                                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mt-1">ACTIVATED</span>
                                    </div>
                                    <div className="w-px h-10 bg-slate-200"></div>
                                    <div className="text-center">
                                        <span className="text-[32px] font-black text-indigo-600 leading-none">02</span>
                                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mt-1">RECOMMENDED</span>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {/* Activated Suites */}
                                <div className="md:col-span-2 grid grid-cols-2 lg:grid-cols-4 gap-4">
                                    {['SRM ARCHITECT', 'INTEL HUB', 'GLOBAL MONITOR', 'ASSET WATCH'].map((suite, i) => (
                                        <div key={suite} className="bg-slate-50 border border-slate-200 p-5 rounded-2xl flex flex-col justify-between hover:border-blue-500/40 transition-colors cursor-help group">
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-600 text-xs font-bold">
                                                    {i + 1}
                                                </div>
                                                <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                                            </div>
                                            <div>
                                                <h5 className="text-[11px] font-black text-slate-900 uppercase tracking-tighter leading-tight group-hover:text-blue-600 transition-colors">{suite}</h5>
                                                <p className="text-[8px] text-slate-500 uppercase tracking-widest mt-1">FULLY DEPLOYED</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Predictive Recommendation */}
                                <div className="bg-indigo-50 border border-indigo-100 p-8 rounded-2xl shadow-sm relative overflow-hidden group">
                                    <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-500/5 blur-3xl -mr-12 -mt-12 transition-all group-hover:scale-150 group-hover:opacity-40"></div>
                                    <div className="relative z-10">
                                        <div className="bg-indigo-100 text-indigo-600 text-[8px] font-black uppercase tracking-[0.3em] px-2 py-1 rounded w-fit mb-6">
                                            AI PREDICTIVE ADVISORY
                                        </div>
                                        <h4 className="text-xl font-black text-slate-900 mb-4 leading-tight">
                                            Priority Suite Expansion:<br/>
                                            <span className="text-indigo-600">Crisis Resilience Suite</span>
                                        </h4>
                                        <p className="text-xs text-slate-600 leading-relaxed mb-6">
                                            Based on regional instability forecasting in {currentOrganization?.region || 'West Africa'}, current capabilities require a 40% upscale in automated evacuation protocols and logistics tracking.
                                        </p>
                                        <button className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-black text-[10px] uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-indigo-600/20 transform active:scale-95">
                                            Activate Extension
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 print:gap-8">
                    {/* Left Column (Existing Methodologies) */}
                    <div className="space-y-12">
                        {/* Summary Block */}
                        <section>
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="w-1 h-8 bg-blue-600"></div>
                                <h2 className="text-xl font-black text-slate-900 print:text-black uppercase tracking-[0.2em]">Contextual Synthesis</h2>
                            </div>
                            <div className="bg-white border border-slate-200 p-10 rounded-3xl leading-relaxed whitespace-pre-wrap relative overflow-hidden shadow-sm">
                                <div className="absolute top-0 right-0 opacity-[0.03] rotate-12 -mr-8 -mt-8">
                                    <svg className="w-48 h-48 text-white" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M14.017 21L14.017 18C14.017 16.8954 13.1216 16 12.017 16H9.01701V13H15.017C16.1216 13 17.017 12.1046 17.017 11V7C17.017 5.89543 16.1216 5 15.017 5H9.01701C7.91244 5 7.01701 5.89543 7.01701 7V13C7.01701 14.1046 7.91244 15 9.01701 15H10.017V16H7.01701C5.91244 16 5.01701 16.8954 5.01701 18V21H14.017Z" />
                                    </svg>
                                </div>
                                <p className="text-lg text-slate-700 font-light italic mb-8 border-l-2 border-indigo-600 pl-6">
                                    "{execSummary}"
                                </p>
                                {subSummary && <p className="text-slate-600 text-sm leading-8">{subSummary}</p>}
                                <div className="mt-10 flex items-center justify-between pt-8 border-t border-slate-100">
                                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Grounding Strategy</span>
                                    <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest">{reportData?.grounding?.industry || 'Standard Sector Analysis'}</span>
                                </div>
                            </div>
                        </section>

                        {Object.keys(pillarAnalysis).length > 0 && (
                            <section>
                                <div className="mb-6 flex items-center space-x-3">
                                    <div className="h-8 w-1 bg-emerald-600" />
                                    <h2 className="text-xl font-black uppercase tracking-[0.2em] text-slate-900">Selected Pillar Analysis</h2>
                                </div>
                                <div className="space-y-5">
                                    {Object.entries(pillarAnalysis).map(([pillar, value]: [string, any]) => (
                                        <div key={pillar} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                                            <h3 className="text-xs font-black uppercase tracking-widest text-emerald-700">
                                                {pillarLabels[pillar] || pillar}
                                            </h3>
                                            <p className="mt-3 text-sm leading-relaxed text-slate-700">{value.summary}</p>
                                            <div className="mt-5 grid gap-5 md:grid-cols-2">
                                                <div>
                                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500">Findings</h4>
                                                    <ul className="mt-2 list-disc space-y-2 pl-5 text-sm text-slate-600">
                                                        {(value.findings || []).map((finding: string, index: number) => <li key={index}>{finding}</li>)}
                                                    </ul>
                                                </div>
                                                <div>
                                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500">Actions</h4>
                                                    <ul className="mt-2 list-disc space-y-2 pl-5 text-sm text-slate-600">
                                                        {(value.recommendations || []).map((recommendation: string, index: number) => <li key={index}>{recommendation}</li>)}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* PESTLE or Custom Modules */}
                        {(config?.methodologies.pestleAnalysis ?? true) && (
                            <section>
                                <div className="flex items-center justify-between mb-8">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-1 h-8 bg-indigo-600"></div>
                                        <h2 className="text-xl font-black text-slate-900 uppercase tracking-[0.2em]">PESTLE Strategic Factors</h2>
                                    </div>
                                    <div className="text-[10px] font-black text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full uppercase tracking-widest border border-indigo-100">
                                        Macro Analysis
                                    </div>
                                </div>
                                {Object.keys(pestleData).length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {Object.entries(pestleData).map(([key, val], idx) => {
                                            const colors = ['border-indigo-100', 'border-emerald-100', 'border-blue-100', 'border-orange-100', 'border-purple-100', 'border-rose-100'];
                                            const textColors = ['text-indigo-600', 'text-emerald-600', 'text-blue-600', 'text-orange-600', 'text-purple-600', 'text-rose-600'];
                                            return (
                                                <div key={key} className={`bg-white border ${colors[idx % colors.length]} p-6 rounded-2xl shadow-sm transition-all hover:scale-[1.02] duration-300 group`}>
                                                    <h3 className={`text-[10px] font-black uppercase tracking-[0.3em] mb-3 ${textColors[idx % textColors.length]}`}>{key}</h3>
                                                    <p className="text-sm text-slate-600 leading-relaxed group-hover:text-slate-900 transition-colors">{val as string}</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="bg-[#0B1121] border border-white/5 p-12 rounded-3xl text-center">
                                        <p className="text-slate-500 text-sm italic font-light">No PESTLE factors identified for the selected parameters.</p>
                                    </div>
                                )}
                            </section>
                        )}

                        {/* PMESII Analysis */}
                        {(config?.methodologies.pmesiiAnalysis ?? true) && (
                            <section className="mt-16">
                                <div className="flex items-center justify-between mb-8">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-1 h-8 bg-amber-600"></div>
                                        <h2 className="text-xl font-black text-slate-900 uppercase tracking-[0.2em]">PMESII Operational Environment</h2>
                                    </div>
                                    <div className="text-[10px] font-black text-amber-600 bg-amber-50 px-3 py-1 rounded-full uppercase tracking-widest border border-amber-100">
                                        Domain Awareness
                                    </div>
                                </div>
                                {Object.keys(pmesiiData).length > 0 ? (
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                                        {Object.entries(pmesiiData).map(([key, data]: [string, any]) => {
                                            const statusColor = data.status === 'Critical' ? 'text-red-600 bg-red-50 border-red-100' : data.status === 'Elevated' ? 'text-amber-600 bg-amber-50 border-amber-100' : 'text-slate-500 bg-slate-50 border-slate-200';
                                            return (
                                                <div key={key} className="bg-white border border-slate-200 p-5 rounded-2xl flex flex-col justify-between hover:bg-slate-50/50 transition-colors group shadow-sm">
                                                    <div>
                                                        <div className="flex justify-between items-center mb-4">
                                                            <h3 className="text-[11px] font-black text-slate-900 uppercase tracking-tighter">{key}</h3>
                                                            <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded border ${statusColor}`}>{data.status}</span>
                                                        </div>
                                                        <p className="text-xs text-slate-600 font-medium leading-relaxed group-hover:text-slate-900 transition-colors">{data.detail}</p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="bg-[#0B1121] border border-white/5 p-12 rounded-3xl text-center">
                                        <p className="text-slate-500 text-sm italic font-light">No PMESII context available for the current operational footprint.</p>
                                    </div>
                                )}
                            </section>
                        )}
                        
                        {/* Recommendations */}
                         <section className="mt-16">
                            <div className="flex items-center space-x-3 mb-8">
                                <div className="w-1 h-8 bg-emerald-600"></div>
                                <h2 className="text-xl font-black text-slate-900 uppercase tracking-[0.2em]">Strategic Countermeasures</h2>
                            </div>
                            <div className="space-y-4">
                                {recs.map((rec: string, i: number) => (
                                    <div key={i} className="flex items-start bg-emerald-50 border-l-4 border-emerald-600 p-6 rounded-r-2xl group hover:bg-emerald-100 transition-all shadow-sm">
                                        <span className="text-emerald-600 mr-4 mt-1 bg-white p-2 rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                                            {ICONS.shield}
                                        </span>
                                        <span className="text-slate-700 group-hover:text-slate-900 transition-colors leading-relaxed font-medium">{rec}</span>
                                    </div>
                                ))}
                            </div>
                        </section>
                        
                        {/* Detailed Intelligence Modules (For Intelligence Reports) */}
                        {reportData?.sections && (
                            <section className="mt-16">
                                <div className="flex items-center space-x-3 mb-8">
                                    <div className="w-1 h-8 bg-blue-600"></div>
                                    <h2 className="text-xl font-black text-slate-900 uppercase tracking-[0.2em]">Detailed Analysis Modules</h2>
                                </div>
                                <div className="space-y-8">
                                    {reportData.sections.map((section: any, idx: number) => (
                                        <div key={idx} className="bg-white border border-slate-200 p-8 rounded-2xl relative overflow-hidden group hover:border-blue-500/30 transition-colors shadow-sm">
                                            <h3 className="text-md font-black text-slate-900 uppercase tracking-widest mb-6 flex items-center">
                                                <span className="w-8 h-8 bg-blue-50/50 text-blue-600 border border-blue-100 rounded-lg flex items-center justify-center text-xs mr-4">{idx + 1}</span>
                                                {section.title}
                                            </h3>
                                            <div className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap">
                                                {section.formData?.content || section.formData?.analysis || section.formData?.executiveSummary || section.formData?.findings || "Module content currently being synthesized..."}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}
                    </div>

                    {/* Right Column (Visual Analytics) */}
                    <div className="space-y-12">
                        
                        {/* Predictive Chart */}
                        {(config?.methodologies.predictiveFootprint ?? true) && (
                            <section>
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Predictive Threat Trajectory</h2>
                                    <span className="text-[9px] font-black text-blue-400 bg-blue-500/10 px-2 py-1 rounded border border-blue-500/20">AI VECTOR FORECAST</span>
                                </div>
                                <div className="h-72 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm relative overflow-hidden group">
                                    <div className="absolute inset-0 bg-gradient-to-b from-blue-500/[0.01] to-transparent pointer-events-none"></div>
                                    {predictiveData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={predictiveData}>
                                                <defs>
                                                    <linearGradient id="colorRisk" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.4}/>
                                                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                                                    </linearGradient>
                                                    <linearGradient id="colorBase" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2}/>
                                                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                                <XAxis 
                                                    dataKey="month" 
                                                    stroke="#94a3b8" 
                                                    fontSize={9} 
                                                    tickLine={false} 
                                                    axisLine={false} 
                                                    tick={{ fontWeight: 800, letterSpacing: '0.1em' }}
                                                />
                                                <YAxis hide />
                                                <Tooltip 
                                                    contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', fontSize: '10px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                                                    itemStyle={{ fontWeight: 800 }}
                                                />
                                                <Area type="monotone" dataKey="baseline" stroke="#6366f1" fillOpacity={1} fill="url(#colorBase)" strokeWidth={3} name="Historical Baseline" />
                                                <Area type="monotone" dataKey="risk" stroke="#ef4444" fillOpacity={1} fill="url(#colorRisk)" strokeWidth={3} name="Predicted Volume" />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="h-full flex items-center justify-center">
                                            <p className="text-slate-600 text-[10px] font-black uppercase tracking-widest text-center">Insufficient historical data to<br/>generate predictive trajectory.</p>
                                        </div>
                                    )}
                                </div>
                                {predictiveData.length > 0 && (
                                    <div className="mt-4 flex items-start space-x-3 p-4 bg-slate-50 border border-slate-200 rounded-xl">
                                        <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1 animate-pulse"></div>
                                        <p className="text-[11px] text-slate-600 leading-relaxed font-medium uppercase tracking-tight">Model indicates a severe divergence from historical baselines, driven by compounding regional instability factors.</p>
                                    </div>
                                )}
                            </section>
                        )}

                        {/* CARVER Matrix */}
                        {(config?.methodologies.carverMatrix ?? true) && (
                            <section className="mt-12">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Asset Criticality (CARVER)</h2>
                                    <div className="w-3 h-3 rounded-full bg-indigo-500/20 flex items-center justify-center">
                                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-500"></div>
                                    </div>
                                </div>
                                <div className="h-80 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm flex flex-col items-center justify-center group overflow-hidden relative">
                                    <div className="absolute -right-20 -bottom-20 w-40 h-40 bg-indigo-500/5 blur-3xl rounded-full"></div>
                                    {reportData?.carverData && carverData.some((d: any) => d.A > 0) ? (
                                        <>
                                            <ResponsiveContainer width="100%" height="90%">
                                                <RadarChart cx="50%" cy="50%" outerRadius="80%" data={carverData}>
                                                    <PolarGrid stroke="#e2e8f0" />
                                                    <PolarAngleAxis dataKey="subject" tick={{ fill: '#94a3b8', fontSize: 9, fontWeight: 800 }} />
                                                    <Radar name="Primary HQ" dataKey="A" stroke="#4f46e5" strokeWidth={3} fill="#4f46e5" fillOpacity={0.1} />
                                                </RadarChart>
                                            </ResponsiveContainer>
                                            <p className="text-[10px] font-black text-slate-600 uppercase tracking-widest mt-2">Vulnerability Vector Visualization</p>
                                        </>
                                    ) : (
                                        <div className="text-center">
                                            <div className="w-12 h-12 border border-white/10 rounded-xl flex items-center justify-center mx-auto mb-4 text-slate-700">
                                                {ICONS.shield}
                                            </div>
                                            <p className="text-slate-600 text-[10px] font-black uppercase tracking-widest">No physical assets identified for<br/>vulnerability assessment.</p>
                                        </div>
                                    )}
                                </div>
                            </section>
                        )}
                    </div>
                </div>

                {/* --- NEW: Operational Impact Matrix (Drill-Down Tabs) --- */}
                <div className="mt-16 pt-8 border-t border-slate-200 print:border-slate-300">
                    <h2 className="text-2xl font-black text-slate-900 print:text-black uppercase tracking-widest mb-2 flex items-center">
                        <span className="text-blue-600 mr-3">{ICONS.shield}</span>
                        Operational Impact Matrix
                    </h2>
                    <p className="text-slate-600 print:text-slate-600 mb-8 max-w-3xl">Drill-down analysis detailing the direct cascading effects of forecasted threat vectors on critical organizational domains.</p>
                    
                    {/* Page Break for Print */}
                    <div className="hidden print:block h-8 w-full" />

                    {/* Desktop Tabs / Hidden in Print */}
                    <div className="flex space-x-2 border-b border-slate-200 mb-8 print:hidden overflow-x-auto custom-scrollbar pb-2">
                        {['operations', 'personnel', 'assets', 'travel', ...((config?.pillars.userStatistics ?? !!reportData?.userEngagement) ? ['userStatistics'] : [])].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveImpactTab(tab === 'userStatistics' ? 'userEngagement' : tab as any)}
                                className={`px-6 py-3 font-bold text-sm uppercase tracking-widest transition-all rounded-t-lg border-b-2 whitespace-nowrap ${
                                    (activeImpactTab === tab || (activeImpactTab === 'userEngagement' && tab === 'userStatistics'))
                                    ? 'text-blue-600 border-blue-600 bg-blue-50' 
                                    : 'text-slate-500 border-transparent hover:text-slate-700 hover:bg-slate-100'
                                }`}
                            >
                                {tab === 'operations' ? 'Current Operations' : tab === 'userStatistics' ? 'Risk Intelligence' : tab}
                            </button>
                        ))}
                    </div>

                    {/* Tab Contents (All block display in Print) */}
                    <div className="space-y-8 print:space-y-12">
                        
                        {/* 1. Current Operations */}
                        <div className={`${activeImpactTab === 'operations' ? 'block animate-fade-in' : 'hidden'} print:block`}>
                            <h3 className="text-lg font-bold text-blue-600 print:text-blue-700 uppercase tracking-widest mb-4 print:border-b print:border-slate-300 print:pb-2">1. Current Operations Impact</h3>
                            {impactMatrix.operations.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {impactMatrix.operations.map((item: any, idx: number) => {
                                        const isCritical = item.level === 'Critical' || item.level === 'Severe';
                                        const isHigh = item.level === 'High' || item.level === 'Elevated' || item.level === 'At-Risk';
                                        const textColor = isCritical ? 'text-red-500' : isHigh ? 'text-amber-500' : 'text-slate-300';
                                        const borderColor = isCritical ? 'border-red-500/30' : '';
                                        
                                        return (
                                            <div key={idx} className={`bg-white border border-slate-200 p-6 rounded-xl shadow-sm print:border print:border-slate-300 ${borderColor}`}>
                                                <div className={`text-xl md:text-2xl lg:text-3xl font-black mb-1 ${textColor}`}>{item.level}</div>
                                                <h4 className={`text-xs font-bold uppercase tracking-widest mb-3 ${isCritical ? 'text-red-600' : 'text-slate-500'}`}>{item.title}</h4>
                                                <p className="text-sm text-slate-700 print:text-slate-700">{item.description}</p>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="glass-panel p-8 text-center text-slate-500 font-mono text-sm">No operational impacts detected for active projects.</div>
                            )}
                        </div>

                        {/* 2. Personnel */}
                        <div className={`${activeImpactTab === 'personnel' ? 'block animate-fade-in' : 'hidden'} print:block`}>
                            <h3 className="text-lg font-bold text-emerald-400 print:text-emerald-700 uppercase tracking-widest mb-4 print:border-b print:border-slate-300 print:pb-2">2. Personnel Exposure</h3>
                            {impactMatrix.personnel.length > 0 ? (
                                <div className="bg-slate-900/50 print:bg-white border border-slate-800 print:border-slate-300 rounded-xl overflow-hidden">
                                    <table className="w-full text-left text-sm">
                                        <thead className="bg-slate-800/80 print:bg-slate-100 text-slate-400 print:text-slate-600 uppercase tracking-widest text-xs">
                                            <tr>
                                                <th className="p-4 rounded-tl-xl">Region / Cohort</th>
                                                <th className="p-4">Headcount</th>
                                                <th className="p-4">Threat Level</th>
                                                <th className="p-4">Mandated Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-800/50 print:divide-slate-200">
                                            {impactMatrix.personnel.map((item: any, idx: number) => {
                                                const tLevel = String(item.threat).toLowerCase();
                                                const isCritical = tLevel.includes('critical') || tLevel.includes('severe');
                                                const isElevated = tLevel.includes('elevated') || tLevel.includes('warning');
                                                const rowBg = isCritical ? 'hover:bg-slate-800/30 bg-red-500/5 print:bg-red-50' : 'hover:bg-slate-800/30';
                                                const badgeColor = isCritical ? 'text-red-500 bg-red-500/10' : isElevated ? 'text-amber-500 bg-amber-500/10' : 'text-green-500 bg-green-500/10';
                                                const actionColor = isCritical ? 'text-red-400 print:text-red-600' : 'text-slate-400';
                                                
                                                return (
                                                    <tr key={idx} className={`${rowBg} transition-colors`}>
                                                        <td className="p-4 font-bold text-slate-200 print:text-slate-800">{item.region}</td>
                                                        <td className="p-4 font-mono">{item.headcount}</td>
                                                        <td className="p-4"><span className={`${badgeColor} font-bold px-2 py-1 rounded text-xs`}>{item.threat}</span></td>
                                                        <td className={`p-4 ${actionColor} ${isCritical ? 'font-medium' : ''}`}>{item.action}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="glass-panel p-8 text-center text-slate-500 font-mono text-sm">No personnel exposure data calculated for the current timeframe.</div>
                            )}
                        </div>

                        {/* 3. Assets */}
                        <div className={`${activeImpactTab === 'assets' ? 'block animate-fade-in' : 'hidden'} print:block`}>
                            <h3 className="text-lg font-bold text-indigo-400 print:text-indigo-700 uppercase tracking-widest mb-4 print:border-b print:border-slate-300 print:pb-2">3. Physical Asset Viability</h3>
                            {impactMatrix.assets.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {impactMatrix.assets.map((item: any, idx: number) => {
                                        const sLevel = String(item.status).toLowerCase();
                                        const isCritical = sLevel.includes('risk') || sLevel.includes('critical');
                                        const borderClass = isCritical ? 'border-l-red-500' : 'border-l-amber-500';
                                        
                                        return (
                                            <div key={idx} className={`bg-white border border-slate-200 p-6 rounded-xl shadow-sm print:border print:border-slate-300 border-l-4 ${borderClass}`}>
                                                <h4 className="text-slate-900 print:text-black font-bold mb-1">{item.name}</h4>
                                                <p className="text-xs text-slate-500 font-mono mb-4">Location: {item.location} // Status: {item.status}</p>
                                                <p className="text-sm text-slate-700 print:text-slate-600 mb-4">{item.detail}</p>
                                                <div className="bg-indigo-50 print:bg-slate-100 p-3 rounded font-mono text-xs text-indigo-700 print:text-indigo-700">
                                                    &gt; RECOMMEND: {item.recommend}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="glass-panel p-8 text-center text-slate-500 font-mono text-sm">No specific asset vulnerabilities identified in active portfolios.</div>
                            )}
                        </div>

                        {/* 4. Travel Management */}
                        <div className={`${activeImpactTab === 'travel' ? 'block animate-fade-in' : 'hidden'} print:block`}>
                            <h3 className="text-lg font-bold text-cyan-700 print:text-cyan-700 uppercase tracking-widest mb-4 print:border-b print:border-slate-300 print:pb-2">4. Travel Management & Itineraries</h3>
                            <div className="bg-white print:bg-white border border-slate-200 print:border-slate-300 p-6 rounded-xl">
                                <div className="flex items-center justify-between mb-6 pb-6 border-b border-slate-200 print:border-slate-200">
                                    <div className="flex items-center">
                                        <div className="bg-cyan-500/20 p-3 rounded-lg mr-4 text-cyan-500 shrink-0">
                                            {ICONS.globe}
                                        </div>
                                        <div>
                                            <h4 className="text-slate-900 print:text-black font-bold">Planned Executive Movements</h4>
                                            <p className="text-sm text-slate-500 mt-1">Total active itineraries: <span className="text-slate-900 font-bold bg-slate-100 px-2 py-0.5 rounded ml-1">{impactMatrix.travel.length}</span></p>
                                        </div>
                                    </div>
                                </div>
                                
                                {impactMatrix.travel.length > 0 ? (
                                    <ul className="space-y-4">
                                        {impactMatrix.travel.map((item: any, idx: number) => {
                                            const status = String(item.status).toLowerCase();
                                            const isReroute = status.includes('reroute') || status.includes('monitor required');
                                            const badgeClass = isReroute ? 'text-red-700 bg-red-50' : 'text-emerald-700 bg-emerald-50';
                                            
                                            return (
                                                <li key={idx} className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 bg-slate-50 print:bg-slate-50 rounded-lg border border-slate-100 print:border print:border-slate-200 gap-4">
                                                    <div>
                                                        <p className="font-bold text-slate-700 print:text-slate-800 text-sm">{item.title}</p>
                                                        <p className="text-xs text-slate-500 font-mono mt-1">{item.dates}</p>
                                                    </div>
                                                    <div className="flex flex-col sm:items-end gap-2 shrink-0">
                                                        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded w-fit ${badgeClass}`}>{item.status}</span>
                                                        <span className="text-xs text-slate-400">{item.detail}</span>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                ) : (
                                    <div className="text-center py-6 text-slate-500 font-mono text-sm">No executive travel itineraries registered for this period.</div>
                                )}
                            </div>
                        </div>

                        {/* 5. User Engagement & Statistics */}
                        <div className={`${activeImpactTab === 'userEngagement' ? 'block animate-fade-in' : 'hidden'} print:block`}>
                            <h3 className="text-lg font-bold text-blue-600 print:text-blue-700 uppercase tracking-widest mb-4 print:border-b print:border-slate-300 print:pb-2">5. User Engagement & System Utilization</h3>
                            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8">
                                <p className="text-slate-700 print:text-slate-700 mb-8 leading-relaxed italic border-l-2 border-blue-600 pl-4">
                                    "{userEngagement.summary}"
                                </p>
                                
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                                    {userEngagement.stats.map((stat: any, idx: number) => (
                                        <div key={idx} className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">{stat.label}</p>
                                            <div className="flex items-end justify-between">
                                                <span className="text-3xl font-black text-slate-900">{stat.value}</span>
                                                <span className={`text-xs font-bold px-2 py-1 rounded ${stat.trend === 'up' ? 'text-emerald-700 bg-emerald-50' : stat.trend === 'down' ? 'text-red-700 bg-red-50' : 'text-slate-500 bg-slate-100'}`}>
                                                    {stat.trend === 'up' ? '↑' : stat.trend === 'down' ? '↓' : '→'}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="space-y-4">
                                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest">High-Volume Workflows</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {userEngagement.topWorkflows.map((wf: string, idx: number) => (
                                            <span key={idx} className="bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest">
                                                {wf}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                {/* --- Contextual Grounding Disclosure --- */}
                <div className="mt-16 bg-blue-50 print:bg-slate-50 border border-blue-100 print:border-slate-300 p-8 rounded-xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100 blur-3xl rounded-full translate-x-1/2 -translate-y-1/2 print:hidden"></div>
                    <h3 className="text-sm font-bold text-blue-700 print:text-blue-700 uppercase tracking-[0.2em] mb-4 flex items-center">
                        <span className="w-2 h-2 bg-blue-600 rounded-full mr-2 shadow-[0_0_8px_rgba(37,99,235,0.3)]"></span>
                        Contextual Grounding Strategy
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div>
                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Primary Grounding Vector</p>
                            <p className="text-slate-800 print:text-slate-800 text-sm font-medium">{reportData?.grounding?.industry || 'General Sector'}</p>
                            <p className="text-slate-500 text-[11px] mt-1 italic">Strategically mapped to sector-specific threat models.</p>
                        </div>
                        <div>
                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Operational Region</p>
                            <p className="text-slate-800 print:text-slate-800 text-sm font-medium">{reportData?.grounding?.region || 'Global'}</p>
                            <p className="text-slate-500 text-[11px] mt-1 italic">Filtered against low-latency humanitarian data feeds.</p>
                        </div>
                        <div>
                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Asset Footprint</p>
                            <p className="text-slate-800 print:text-slate-800 text-sm font-medium">
                                {(reportData?.grounding?.assetCount || 0) + (reportData?.grounding?.frameworkCount || 0)} Verifiable Parameters
                            </p>
                            <p className="text-slate-500 text-[11px] mt-1 italic">Intersectional analysis between internal assets and external risks.</p>
                        </div>
                    </div>
                </div>

                {/* Footer Footer */}
                <div className="mt-8 pt-8 border-t border-slate-200 print:border-slate-300 flex justify-between items-center text-xs text-slate-500">
                    <p>IntelOptics Insights • Enterprise Control Center</p>
                    <p className="font-mono uppercase">End of Report</p>
                </div>
            </div>
        </div>
    );
};
